import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useLanguage } from '../../context/LanguageContext'
import { AppLayout } from '../../components/layouts/AppLayout'
import { MetricCard } from '../../components/dashboard/MetricCard'
import { Button } from '../../components/ui/button'
import { Package, Boat, FileText, ChatCircle, Plus, Spinner, Bell, Warning, Prohibit, Trash } from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'
import api from '../../services/api'
import { useNotifications } from '../../hooks/useNotifications'
import toast from 'react-hot-toast'

interface ShipperStats {
  activeQuotes: number
  quotesThisMonth: number
  totalQuotes: number
  offersReceived: number
  pendingOffers: number
  activeOrders: number
  canceledQuotes: number
  deletedQuotes: number
}

interface RecentQuote {
  id: string
  referenceCode: string
  modality: string
  type: string
  status: string
  originCity: string
  originCountry: string
  destinationCity: string
  destinationCountry: string
  createdAt: string
  offersCount: number
}

export default function ShipperDashboard() {
  const { user } = useAuth()
  const { t } = useLanguage()
  const navigate = useNavigate()
  // Shipper pending Eximtech approval can't create quotes yet.
  const shipperPending = user?.current_company?.type === 'shipper' && user?.current_company?.is_approved === false

  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<ShipperStats | null>(null)
  const [recentQuotes, setRecentQuotes] = useState<RecentQuote[]>([])
  const [error, setError] = useState<string | null>(null)

  // Fetch data function (extractable for refresh)
  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      // Buscar stats e cotações recentes em paralelo
      const [statsRes, quotesRes] = await Promise.all([
        api.get('/shipper/stats'),
        api.get('/shipper/quotes/recent')
      ])

      setStats(statsRes.data.stats)
      setRecentQuotes(quotesRes.data.quotes)
    } catch (err: any) {
      console.error('Error fetching shipper data:', err)
      setError(err.response?.data?.error || 'Erro ao carregar dados')
    } finally {
      setLoading(false)
    }
  }, [])

  // Handle new notification - show toast and refresh data
  const handleNewNotification = useCallback((notification: any) => {
    // Show custom toast
    toast.custom((t) => (
      <div
        className={`${
          t.visible ? 'animate-enter' : 'animate-leave'
        } max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}
      >
        <div className="flex-1 w-0 p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0 pt-0.5">
              <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <Bell size={20} className="text-emerald-600" weight="fill" />
              </div>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-900">
                {notification.title}
              </p>
              <p className="mt-1 text-sm text-gray-500">
                {notification.message}
              </p>
            </div>
          </div>
        </div>
        <div className="flex border-l border-gray-200">
          <button
            onClick={() => {
              toast.dismiss(t.id)
              if (notification.type === 'new_offer') {
                navigate('/shipper/offers')
              }
            }}
            className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-emerald-600 hover:text-emerald-500 focus:outline-none"
          >
            Ver
          </button>
        </div>
      </div>
    ), {
      duration: 6000,
      position: 'top-right',
    })

    // Refresh dashboard data when notification arrives
    fetchData()
  }, [navigate, fetchData])

  // Real-time notifications
  const { unreadCount, connected } = useNotifications({
    onNewNotification: handleNewNotification
  })

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; color: string }> = {
      draft: { label: 'Rascunho', color: 'bg-gray-100 text-gray-700' },
      open: { label: 'Aberta', color: 'bg-blue-100 text-blue-700' },
      in_analysis: { label: 'Em Análise', color: 'bg-yellow-100 text-yellow-700' },
      quoted: { label: 'Cotada', color: 'bg-emerald-100 text-emerald-700' },
      accepted: { label: 'Aceita', color: 'bg-green-100 text-green-700' },
      completed: { label: 'Aprovada', color: 'bg-gray-100 text-gray-700' },
      cancelled: { label: 'Cancelada', color: 'bg-red-100 text-red-700' },
      expired: { label: 'Expirada', color: 'bg-orange-100 text-orange-700' },
    }
    const config = statusConfig[status] || { label: status, color: 'bg-gray-100 text-gray-700' }
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
        {config.label}
      </span>
    )
  }

  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {t.dashboard.title}
              </h1>
              <p className="text-sm text-gray-500">
                {t.dashboard.welcome}, {user?.name}
              </p>
            </div>
            <Button
              onClick={() => navigate('/shipper/quotes/new')}
              size="lg"
              className="gap-2"
              disabled={shipperPending}
              title={shipperPending ? 'Cadastro em análise pela equipe Eximtech' : undefined}
            >
              <Plus size={20} />
              Nova Cotação
            </Button>
          </div>
        </div>
      </header>

      {shipperPending && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
            <Warning size={22} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800">Cadastro em análise</p>
              <p className="text-sm text-amber-700 mt-1">
                A equipe Eximtech está revisando seu cadastro. Assim que for liberado, você poderá criar cotações.
                Enquanto isso, complete seu perfil e cadastre seus agentes de carga.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Spinner size={32} className="animate-spin text-emerald-600" />
            <span className="ml-3 text-gray-500">Carregando dados...</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-600">{error}</p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => window.location.reload()}
            >
              Tentar novamente
            </Button>
          </div>
        )}

        {/* Stats */}
        {stats && (
          <>
            {/* Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <MetricCard
                title="Cotações Ativas"
                value={stats.activeQuotes}
                subtitle={`${stats.totalQuotes} total`}
                icon={Package}
                iconColor="rgb(5, 150, 105)"
                onClick={() => navigate('/shipper/quotes')}
              />
              <MetricCard
                title="Ofertas Recebidas"
                value={stats.offersReceived}
                subtitle={`${stats.pendingOffers} pendentes`}
                icon={FileText}
                iconColor="rgb(59, 130, 246)"
                onClick={() => navigate('/shipper/offers')}
              />
              <MetricCard
                title="Cotações Este Mês"
                value={stats.quotesThisMonth}
                subtitle="novas solicitações"
                icon={ChatCircle}
                iconColor="rgb(251, 146, 60)"
                onClick={() => navigate('/shipper/quotes')}
              />
              <MetricCard
                title="Embarques Ativos"
                value={stats.activeOrders}
                subtitle="em andamento"
                icon={Boat}
                iconColor="rgb(139, 92, 246)"
                onClick={() => navigate('/shipper/shipments')}
              />
            </div>

            {/* Informe: cotações canceladas / excluídas (Plane #23) */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 mb-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900">Cotações canceladas / excluídas</h3>
                <button
                  onClick={() => navigate('/shipper/quotes')}
                  className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                >
                  Ver cotações
                </button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => navigate('/shipper/quotes?status=canceled')}
                  className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:border-red-200 hover:bg-red-50 transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-full bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0">
                    <Prohibit size={20} weight="duotone" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{stats.canceledQuotes}</div>
                    <div className="text-xs text-gray-500">canceladas</div>
                  </div>
                </button>
                <button
                  onClick={() => navigate('/shipper/quotes?status=deleted')}
                  className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:border-gray-300 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center flex-shrink-0">
                    <Trash size={20} weight="duotone" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{stats.deletedQuotes}</div>
                    <div className="text-xs text-gray-500">excluídas</div>
                  </div>
                </button>
              </div>
            </div>

            {/* Recent Quotes Section */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Cotações Recentes
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      Suas últimas solicitações de frete
                    </p>
                  </div>
                  <Button variant="outline" onClick={() => navigate('/shipper/quotes')}>
                    Ver Todas
                  </Button>
                </div>
              </div>
              <div className="p-6">
                {recentQuotes.length === 0 ? (
                  <div className="text-center py-12">
                    <Package size={48} className="mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500 mb-4">Nenhuma cotação encontrada</p>
                    <Button onClick={() => navigate('/shipper/quotes/new')}>
                      <Plus size={16} className="mr-2" />
                      Criar Primeira Cotação
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recentQuotes.map((quote) => (
                      <div
                        key={quote.id}
                        className="flex items-center justify-between p-4 rounded-lg border border-gray-100 hover:border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => navigate(`/shipper/quotes/${quote.id}`)}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                            <Package size={20} className="text-emerald-600" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-gray-900">
                                {quote.referenceCode || `#${quote.id.slice(0, 8)}`}
                              </p>
                              {getStatusBadge(quote.status)}
                            </div>
                            <p className="text-sm text-gray-500">
                              {quote.originCity}, {quote.originCountry} → {quote.destinationCity}, {quote.destinationCountry}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">
                            {quote.offersCount} {quote.offersCount === 1 ? 'oferta' : 'ofertas'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(quote.createdAt).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </main>
    </AppLayout>
  )
}
